import { Fragment, memo, useCallback, useState, useEffect } from "react";
import { logActivity } from "../services/LogService";
import { Dialog, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  HomeIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChatBubbleBottomCenterTextIcon,
  PlusCircleIcon,
  UserIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  MegaphoneIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import SidebarItem from "./SidebarItem";

const currentNav = [
  { name: "Dashboard", href: "/", alias: "#dashboard", icon: HomeIcon, current: false, allowedRoles: ['Admin', 'Doctor', 'SuperAdmin'] },
  { name: "Create a Blog", href: "/create", alias: "#createNews", icon: PlusCircleIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
  { name: "Published Blogs", href: "/blogs", alias: "#newsSearch", icon: MagnifyingGlassIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
  { name: "Doctor Questions", href: "/doctor/ayubo", alias: "#doctor", icon: ChatBubbleBottomCenterTextIcon, current: false, allowedRoles: ['Admin', 'Doctor', 'SuperAdmin'] },
  { name: "Doctor Analytics", href: "/doctor/analytics", alias: "#doctorAnalytics", icon: ChartBarIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
  {
    name: "Community Section",
    alias: "#community",
    icon: UserGroupIcon,
    current: false,
    allowedRoles: ['Admin', 'Doctor', 'SuperAdmin'],
    children: [
      { name: "All Community Posts", href: "/community/all", alias: "#communityAll", current: false },
      { name: "Posts & Comments Approve", href: "/community/approve", alias: "#communityApprove", current: false },
      { name: "Community Categories", href: "/community/categories", alias: "#communityCategories", current: false },
      { name: "Blacklisted Mobile Users", href: "/community/blacklisted", alias: "#communityBlacklisted", current: false },
      { name: "Unauthorized Texts Management", href: "/community/manage/texts", alias: "#communityUnauthorizedTexts", current: false },
      { name: "Export Community Data", href: "/community/export", alias: "#communityExportData", current: false },
    ],
  },
  {
    name: "Report Section",
    alias: "#reports",
    icon: ChartBarIcon,
    current: false,
    allowedRoles: ['Admin', 'SuperAdmin'],
    children: [
      { name: "Chat Report", href: "/report", alias: "#chatReport", current: false },
      { name: "Community Report", href: "/reportcom", alias: "#communityReport", current: false },
    ],
  },
  { name: "Notifications", href: "/notifications", alias: "#notifications", icon: ChatBubbleBottomCenterTextIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
  { name: "In-App Banners", href: "/banners", alias: "#banners", icon: MegaphoneIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
  { name: "Category Manager", href: "/categories", alias: "#categoryManager", icon: PlusCircleIcon, current: false, allowedRoles: ['SuperAdmin'] },
  { name: "Report Recipients", href: "/report-recipients", alias: "#reportRecipients", icon: EnvelopeIcon, current: false, allowedRoles: ['SuperAdmin'] },
  { name: "User Activity Logs", href: "/activity-logs", alias: "#activityLogs", icon: ClipboardDocumentCheckIcon, current: false, allowedRoles: ['SuperAdmin'] },
  { name: "User", href: "/user", alias: "#user", icon: UserIcon, current: false, allowedRoles: ['Admin', 'SuperAdmin'] },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Sidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigation, setNavigation] = useState([]);
  const [headerTitle, setHeaderTitle] = useState("Dashboard");
  const navigate = useNavigate();
  const signOut = useSignOut();
  const auth = useAuthUser();

  useEffect(() => {
    if (auth) {
      const userType = auth?.type || "Admin"; // Fallback to Admin if missing
      const filteredNav = currentNav.filter(item => item.allowedRoles.includes(userType));
      setNavigation(filteredNav);
    }
  }, [auth]);

  const handleMenuItemClick = useCallback(
    (selectedItem) => {
      const newNav = navigation.map((menuItem) => {
        const isParentMatch = menuItem.alias === selectedItem.alias;
        const hasChildren = !!menuItem.children;

        if (!hasChildren) {
          return {
            ...menuItem,
            current: isParentMatch,
          };
        }

        const updatedChildren = menuItem.children.map((subItem) => ({
          ...subItem,
          current: subItem.alias === selectedItem.alias,
        }));

        const isAnyChildActive = updatedChildren.some((sub) => sub.current);

        return {
          ...menuItem,
          current: isParentMatch || isAnyChildActive,
          children: updatedChildren,
        };
      });

      setNavigation(newNav);
      setHeaderTitle(selectedItem.name);
      navigate(selectedItem.href);
      setSidebarOpen(false);
    },
    [navigation, navigate]
  );

  const signOutFunctionHandler = async () => {
    try {
      await logActivity("USER_LOGOUT", `User logged out: ${auth?.email || 'Unknown'}`);
    } catch (e) {
      console.error(e);
    }
    navigate("/login");
    signOut();
  };

  return (
    <>
      {/* BACKGROUND GRADIENT AURA (desktop) */}
      <div className="pointer-events-none fixed inset-0 z-0 hidden lg:block">
        <div className="absolute -left-28 top-10 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute -left-10 bottom-10 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
      </div>

      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                {/* Close button */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 rounded-full bg-slate-900/80 p-2.5 text-slate-200 shadow-lg shadow-black/40 ring-1 ring-white/10"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                {/* Panel */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-slate-950/95 px-5 pb-4 pt-4 ring-1 ring-slate-800/80 backdrop-blur-2xl">

                  <div className="flex h-14 shrink-0 items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img className="h-8 w-auto" src="/images/fems-fio-logo-03.png" alt="Fio Admin" />
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase">
                          FIO
                        </span>
                        <span className="text-[11px] text-slate-200">
                          Admin Console
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Navigation */}
                  <nav className="flex flex-1 flex-col">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Menu
                    </p>
                    <ul role="list" className="flex flex-1 flex-col gap-y-6">
                      <li>
                        <ul role="list" className="-mx-1 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <SidebarItem item={item} onItemClick={handleMenuItemClick} />
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>

                  {/* Profile + logout */}
                  <div className="-mx-3 mt-4 border-t border-slate-800/80 pt-4">
                    <div className="flex items-center gap-3 rounded-2xl bg-surface/80 px-3 py-3 ring-1 ring-white/5 shadow-sm shadow-black/40">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-orange-500 text-xs font-semibold text-background">
                        {auth?.name?.[0] || "A"}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-slate-100 truncate">
                          {auth?.name}
                        </span>
                        <span className="text-[11px] text-slate-400 truncate">
                          {auth?.email}
                        </span>
                        <span className="mt-0.5 inline-flex w-fit rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium text-primary-300 ring-1 ring-primary-500/40">
                          {auth?.type || "Admin"}
                        </span>
                      </div>
                    </div>
                    <button
                      className="mt-3 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-red-500 to-rose-500 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-red-900/50 transition hover:from-red-400 hover:to-rose-400"
                      onClick={signOutFunctionHandler}
                    >
                      Log out
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-6 overflow-y-auto bg-background/95 px-5 pb-5 pt-4 border-r border-slate-800/80 backdrop-blur-2xl shadow-xl shadow-black/50">
          {/* Logo + title */}
          <div className="flex h-14 shrink-0 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-surface ring-1 ring-primary-500/40 ">
                <img className="h-14 w-auto" src="/images/fems-fio-logo-03.png" alt="Fio Admin" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase">
                  FIO
                </span>
                <span className="text-[11px] text-slate-200">
                  Admin Control Center
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Main
            </p>
            <ul role="list" className="flex flex-1 flex-col gap-y-5">
              <li>
                <ul role="list" className="-mx-1 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <SidebarItem item={item} onItemClick={handleMenuItemClick} />
                    </li>
                  ))}
                </ul>
              </li>

              {/* Profile */}
              <li className="mt-auto -mx-3 pt-4 border-t border-slate-800/80">
                <div className="flex items-center gap-3 rounded-2xl bg-surface/80 px-3 py-3 ring-1 ring-white/5 shadow-sm shadow-black/40">
                  <div className="relative">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-orange-500 text-xs font-semibold text-background">
                      {auth?.name?.[0] || "A"}
                    </div>
                    <span className="absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border border-surface bg-emerald-400" />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-xs font-semibold text-slate-100 truncate">
                      {auth?.name}
                    </span>
                    <span className="text-[11px] text-slate-400 truncate">
                      {auth?.email}
                    </span>
                    <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium text-primary-300 ring-1 ring-primary-500/40">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />
                      {auth?.type || "Admin"}
                    </span>
                  </div>
                </div>
                <button
                  className="mt-3 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-red-500 to-rose-500 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-red-900/50 transition hover:from-red-400 hover:to-rose-400"
                  onClick={signOutFunctionHandler}
                >
                  Log out
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Top bar (mobile) */}
      <div className="sticky top-0 z-30 flex items-center gap-x-4 bg-slate-950/95 px-4 py-3 shadow-lg shadow-black/40 ring-1 ring-slate-800/80 sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 inline-flex items-center justify-center rounded-lg p-2.5 text-slate-300 ring-1 ring-slate-700/80 bg-slate-900/80"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="flex-1 text-sm font-semibold leading-6 text-slate-100">
          {headerTitle}
        </div>
        <div className="flex items-center">
          <span className="sr-only">Your profile</span>
          <img
            className="h-8 w-8 rounded-full ring-1 ring-slate-600/70 bg-slate-800 object-cover"
            src="/icons/user.jpg"
            alt="user"
          />
        </div>
      </div>
    </>
  );
};

export default memo(Sidebar);
